import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Statistic, Tabs, Alert, notification } from 'antd';
import { 
  UserOutlined, GlobalOutlined, NotificationOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import Navbar from '../Layout/Navbar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Topbar from '../Layout/Topbar';
import PlansList from '../../components/PlansList';
import ProfileSettings from '../../components/ProfileSettings';
import TestimonialsList from '../../components/TestimonialsList';
import api from '../../services/api';
import '../../dashboard.css';

const { Content } = Layout;
const { TabPane } = Tabs;

const UserDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [user, setUser] = useState({});
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState([]);
  const [planAlerts, setPlanAlerts] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user')) || {};
    setUser(userData);
    fetchDashboardStats(userData.type);
    fetchPlans();
    checkPlanExpiry();
    
    // Handle payment callback from URL parameters
    handlePaymentCallback();
    
    // Check for alerts every hour
    const interval = setInterval(checkPlanExpiry, 3600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // totalPlans was computed but never rendered anywhere - removed.
  const handlePaymentCallback = () => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get('payment');
    const transactionId = urlParams.get('transactionId');
    const reason = urlParams.get('reason');
    const message = urlParams.get('message');
    
    if (paymentStatus) {
      // Clean URL immediately
      navigate('/dashboard', { replace: true });
      
      if (paymentStatus === 'success' && transactionId) {
        // Show success notification
        notification.success({
          message: 'Payment Successful!',
          description: `Your payment has been processed successfully. Transaction ID: ${transactionId}`,
          duration: 10,
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        });
        
        // Verify payment and refresh plans
        verifyPaymentAndRefresh(transactionId);
        
        // Switch to plans tab to show updated status
        setActiveTab('plans');
        
      } else if (paymentStatus === 'failed') {
        const errorMsg = reason ? decodeURIComponent(reason) : 'Payment failed. Please try again.';
        notification.error({
          message: 'Payment Failed',
          description: errorMsg,
          duration: 10,
          icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
        });
        
      } else if (paymentStatus === 'error') {
        let errorMsg = 'An error occurred during payment processing.';
        if (message === 'status_check_failed') {
          errorMsg = 'Payment status verification failed. Please contact support.';
        } else if (message === 'missing_transaction') {
          errorMsg = 'Transaction information missing. Please try again.';
        } else if (message === 'callback_error') {
          errorMsg = 'Payment callback processing error. Please check your plan status.';
        }
        
        notification.error({
          message: 'Payment Error',
          description: errorMsg,
          duration: 10,
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        });
      }
    }
  };

  const verifyPaymentAndRefresh = async (transactionId) => {
    try {
      const response = await api.get(`/user/payment-status/${transactionId}`);
      if (response.data.success && response.data.data.transaction.status === 'SUCCESS') {
        // Refresh plans and alerts
        fetchPlans();
        checkPlanExpiry();
        
        notification.info({
          message: 'Plan Activated',
          description: 'Your plan has been activated and is now available.',
          duration: 5,
        });
      } else {
        notification.warning({
          message: 'Payment Verification',
          description: 'Payment verification is still processing. Please refresh the page in a few minutes.',
          duration: 8,
        });
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      notification.warning({
        message: 'Verification Error',
        description: 'Could not verify payment status. Please check your active plans or contact support.',
        duration: 8,
      });
    }
  };

const fetchDashboardStats = async (userType) => {
  try {
    const response = await api.get(`/user/dashboard/stats?type=${userType}`);
    if (response.data.success && response.data.stats) {
      setStats(response.data.stats); // ✅ only store stats object
    } else {
      setStats({});
    }
  } catch (error) {
    console.error('Error fetching stats:', error);
    setStats({});
  }
};


  const fetchPlans = async () => {
    try {
      const response = await api.get('/user/matching-plans');
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setPlans([]);
    }
  };

  const checkPlanExpiry = async () => {
    try {
      const response = await api.get('/user/plan-alerts');
      if (response.data.success && response.data.alerts.length > 0) {
        setPlanAlerts(response.data.alerts);
        
        // Show notifications for each alert (only if not handling payment callback)
        const urlParams = new URLSearchParams(location.search);
        if (!urlParams.get('payment')) {
          response.data.alerts.forEach(alert => {
            notification.warning({
              message: 'Plan Expiry Alert',
              description: alert.message,
              duration: 8,
              icon: <NotificationOutlined />,
            });
          });
        }
      } else {
        setPlanAlerts([]);
      }
    } catch (error) {
      console.error('Error checking plan expiry:', error);
    }
  };

  const handleToggle = () => setCollapsed(!collapsed);
  const handleTabChange = (key) => setActiveTab(key);

  const renderStats = () => {
    return (
      <Row gutter={[16, 16]}>
        {planAlerts.length > 0 && (
          <Col span={24}>
            <Alert
              message="Plan Expiry Notice"
              description={
                <ul style={{ marginBottom: 0 }}>
                  {planAlerts.map((alert, index) => (
                    <li key={index}>{alert.message}</li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
              closable
              onClose={() => setPlanAlerts([])}
            />
          </Col>
        )}
        <Col span={24}>
          <Card>
            <Statistic title="Welcome" value={`${user.name || 'User'}`} prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Navbar collapsed={collapsed} onCollapse={handleToggle} activeTab={activeTab} />
      <Layout>
        <Topbar />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane tab="Dashboard" key="dashboard">
              <div className="dashboard-container">
                <div style={{ marginBottom: '20px' }}>
                  <h3>Welcome, {user.name || 'User'}</h3>
                </div>

                {renderStats()}

                <Card style={{ marginTop: 20, backgroundColor: '#f0f8ff', borderRadius: '8px' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '20px',
                      padding: '20px',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 8px rgba(0, 123, 255, 0.2)',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                      }}
                    >
                      <h4>Total Enquiries</h4>
                      <p style={{ fontSize: '24px', margin: 0, color: '#1890ff' }}>{stats.enquiries || 0}</p>
                    </div>

                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 8px rgba(0, 123, 255, 0.2)',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                      }}
                    >
                      <h4>Total Testimonials</h4>
                      <p style={{ fontSize: '24px', margin: 0, color: '#52c41a' }}>{stats.testimonials || 0}</p>
                    </div>

                    <div
                      style={{
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 8px rgba(0, 123, 255, 0.2)',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                      }}
                    >
                      <h4>Total Active Plans</h4>
                    <p style={{ fontSize: '24px', margin: 0, color: '#faad14' }}>{stats.activePlans || 0}</p>




                    </div>
                  </div>
                </Card>
              </div>
            </TabPane>

            <TabPane tab="Plans" key="plans">
              {plans.length > 0 ? (
                <PlansList plans={plans} user={user} onPlanUpdate={fetchPlans} />
              ) : (
                <Alert 
                  message="No Plans Available" 
                  description="There are currently no plans available for your user type." 
                  type="info" 
                  showIcon
                />
              )}
            </TabPane>

            <TabPane tab="Profile & Settings" key="profile">
              <ProfileSettings user={user} />
            </TabPane>

            <TabPane tab="Testimonials" key="testimonials">
              <TestimonialsList />
            </TabPane>

            <TabPane tab="Visit Website" key="website">
              <Link to="/" target='_blank' className='btn btn-primary'>
                <GlobalOutlined /> 
                &nbsp; Visit Website
              </Link>
            </TabPane>
          </Tabs>
        </Content>
      </Layout>
    </Layout>
  );
};

export default UserDashboard;